// The pop-out window: opened from a click (the browser requires one), closed
// by the person, by leaving the call, or by the page going away. Closing it
// only drops the state; nothing in the call was moved into it, so everything
// is already back where it was.

import { useCallback, useEffect, useRef, useState } from "react";
import { openPopoutWindow, popoutSupported } from "../lib/pip";

export interface PopoutHandle {
  readonly supported: boolean;
  /** The pop-out's body, once open; null while closed. */
  readonly root: HTMLElement | null;
  readonly open: () => Promise<void>;
  readonly close: () => void;
}

const WIDTH = 360;
const HEIGHT = 440;

export function usePopout(): PopoutHandle {
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const winRef = useRef<Window | null>(null);
  const opening = useRef(false);
  const supported = popoutSupported();

  const drop = useCallback((): void => {
    winRef.current = null;
    setRoot(null);
  }, []);

  const close = useCallback((): void => {
    const win = winRef.current;
    drop();
    try {
      win?.close();
    } catch {
      // Already gone.
    }
  }, [drop]);

  const open = useCallback(async (): Promise<void> => {
    if (winRef.current || opening.current) return;
    opening.current = true;
    try {
      const win = await openPopoutWindow(WIDTH, HEIGHT);
      const body = win.document.body;
      body.style.margin = "0";
      body.style.background = "#0b0e12";
      const mount = win.document.createElement("div");
      mount.id = "popout-root";
      body.appendChild(mount);
      winRef.current = win;
      const gone = (): void => {
        if (winRef.current === win) drop();
      };
      win.addEventListener("pagehide", gone);
      setRoot(mount);
    } catch {
      // The person dismissed it, or the browser refused. Nothing moved.
      drop();
    } finally {
      opening.current = false;
    }
  }, [drop]);

  // A window closed by its own X fires pagehide; this covers a browser that
  // closes it without one.
  useEffect(() => {
    if (!root) return;
    const timer = setInterval(() => {
      if (winRef.current?.closed) drop();
    }, 700);
    return () => clearInterval(timer);
  }, [root, drop]);

  useEffect(
    () => () => {
      try {
        winRef.current?.close();
      } catch {
        // Already gone.
      }
      winRef.current = null;
    },
    [],
  );

  return { supported, root, open, close };
}
